import React, { useState } from "react";
import {
  Button,
  FormControlLabel,
  FormGroup,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Container,
} from "@material-ui/core";
import styled from "styled-components";
import {
  CTextField,
  CSelect,
  CAutocomplete,
  HFlex,
  Pane,
  ProgressButton,
  Text,
  DialogSuccessNotice,
  useErrorContext,
  ErrorBox,
  MultiSelect,
} from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { PhoneNumberField, withOptionalPhone } from "shared/PhoneNumberInput";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import { Country, isUsMarket, TimeZone } from "utils/market";
import { capitalizeFirstLetter } from "utils/misc";
import { css } from "@emotion/css";
import { useStates } from "shared/ValueTable";
import { useMountState } from "utils/hooks";
import useInviteTherapist, { Vars } from "routes/therapistSearch/queries/useInviteTherapist";
import { useHCPs } from "api/hooks/useHCPs";
import { countriesEU, timeZonesEU, timeZonesUS } from "../helpers";

interface Props {
  onClose: () => void;
}

const professionsEU = [
  { value: "physical_therapist", label: "Physical Therapist" },
  { value: "occupational_therapist", label: "Occupational Therapist" },
  { value: "speech_therapist", label: "Speech Therapist" },
  { value: "wellness_therapist", label: "Wellness Therapist" },
];

const professionsUS = [
  { value: "physical_therapist", label: "Physical Therapist" },
  { value: "occupational_therapist", label: "Occupational Therapist" },
  { value: "physical_therapist_assistant", label: "Physical Therapist Assistant" },
  { value: "speech_therapist", label: "Speech Therapist" },
  { value: "wellness_therapist", label: "Wellness Therapist" },
  { value: "physician", label: "Physician" },
];

const roleOptions = [
  { value: "therapist", label: "Therapist" },
  { value: "hcp_admin", label: "HCP Admin" },
];

const therapistTypeOptions = [
  { value: "virtual", label: "Virtual" },
  { value: "clinical", label: "Clinical" },
  { value: "hybrid", label: "Hybrid" },
];

const treatmentEnrollments = [
  "hip_treatment_enrollment_enabled",
  "knee_treatment_enrollment_enabled",
  "back_treatment_enrollment_enabled",
  "hand_treatment_enrollment_enabled",
  "shoulder_treatment_enrollment_enabled",
  "neck_treatment_enrollment_enabled",
  "foot_treatment_enrollment_enabled",
  "elbow_treatment_enrollment_enabled",
  "lower_leg_treatment_enrollment_enabled",
  "wrist_treatment_enrollment_enabled",
  "fall_prevention_treatment_enrollment_enabled",
  "osteoporosis_treatment_enrollment_enabled",
] as const;

const parseJointName = (joint: string) => {
  const parts = joint.split("_");
  if (parts[1] !== "treatment") {
    return `${parts[0]} ${parts[1]}`;
  }
  return parts[0];
};

const StyledFormGroup = styled(FormGroup)`
  justify-content: space-evenly;
`;

export const InviteTherapistForm: React.VFC<Props> = ({ onClose }) => {
  const form = useForm<Vars>();
  const { t } = useTranslation();
  const { mutateAsync, isLoading, isSuccess } = useInviteTherapist();
  const setError = useErrorContext();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState<string>();
  const [therapist, setTherapist] = useState<{ external_uuid: string } | null>(null);
  const [hcp, setHcp] = useState<{ name: string } | null>(null);
  const [joints, setJoints] = useState<Record<string, boolean>>(
    Object.fromEntries(treatmentEnrollments.map(j => [j, false]))
  );
  const licenses = useStates();
  const { data: hcps } = useHCPs();
  const mappedHCPs =
    hcps
      ?.map(hcp => ({ value: hcp.name ?? "", label: hcp.name ?? "" }))
      ?.sort((a, b) => (a.label ?? "").toLowerCase().localeCompare((b.label ?? "").toLowerCase())) || [];
  const getMountState = useMountState();

  const availableForPatients = form.watch("available_for_patients");

  const [step1Error, setStep1Error] = useState(false);

  const goNext = () => {
    const v = form.getValues() as any;
    const empty = (val: any) => !val || (Array.isArray(val) && val.length === 0) || val === "";
    const required = ["email", "timezone", "first_name", "last_name", "profession", "roles", "therapist_type"];
    if (isUsMarket) required.push("us_licenses");
    else required.push("country");
    const valid = required.every(f => !empty(v[f]));
    setStep1Error(!valid);
    if (valid) setStep(2);
  };

  const submit = form.handleSubmit(async fields => {
    setEmail(fields.email);
    const hcpName = hcps?.find(hcp => hcp.name === fields.health_care_provider_name)?.name;

    try {
      const response = await mutateAsync({
        ...withOptionalPhone(fields),
        ...joints,
        available_for_patients: !!fields.available_for_patients,
        health_care_provider_new: false,
        health_care_provider_name: hcpName ? hcpName : fields.health_care_provider_name,
      });
      if (!getMountState()) return;

      const res = await response.json();

      setHcp({ name: res.health_care_provider.name });
      setTherapist({ external_uuid: res.external_uuid });
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (isSuccess && hcp && therapist)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${email}. HCP ${hcp.name} UUID ${therapist.external_uuid}`}
        onClose={onClose}
      />
    );

  return (
    <Container>
      <Text heading bold mb={0} id="invite-pt">
        {t("form.invite_therapist")}
      </Text>
      <Stepper activeStep={step - 1} alternativeLabel style={{ padding: "12px 0" }}>
        <Step>
          <StepLabel>Therapist Details</StepLabel>
        </Step>
        <Step>
          <StepLabel>Organization</StepLabel>
        </Step>
      </Stepper>
      <FormProvider {...form}>
        <form noValidate onSubmit={submit}>
          {/* Step 1: Therapist Details */}
          <div style={{ display: step === 1 ? undefined : "none" }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <CTextField
                  name="first_name"
                  autoFocus
                  label={t("form.first_name")}
                  margin="dense"
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <CTextField
                  name="last_name"
                  label={t("form.last_name")}
                  margin="dense"
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <CTextField
                  required
                  name="email"
                  label={t("form.email")}
                  type="email"
                  margin="dense"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <PhoneNumberField
                  containerStyle={{ width: "100%", margin: "8px 0 4px" }}
                  inputStyle={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12}>
                <CSelect
                  name="profession"
                  label={t("form.profession")}
                  items={isUsMarket ? professionsUS : professionsEU}
                  margin="dense"
                  size="small"
                  required
                  fullWidth
                  defaultValue={"physical_therapist"}
                >
                  {({ label }) => <Text>{label}</Text>}
                </CSelect>
              </Grid>
              <Grid item xs={12}>
                <MultiSelect
                  getOptionLabel={(opt: { value: string; label: string }) => opt.label}
                  getOptionValue={(opt: { value: string; label: string }) => opt.value}
                  name="roles"
                  options={roleOptions}
                  label={t("form.roles")}
                  margin="dense"
                  required
                  defaultValue={["therapist"]}
                />
              </Grid>
              <Grid item xs={12}>
                <CSelect
                  name="therapist_type"
                  label={t("form.therapist_type")}
                  items={therapistTypeOptions}
                  margin="dense"
                  size="small"
                  fullWidth
                  required
                  defaultValue="virtual"
                >
                  {({ label }: { label: string }) => <Text>{label}</Text>}
                </CSelect>
              </Grid>
              <Grid item xs={12}>
                <CSelect
                  name="timezone"
                  label={t("form.home_timezone")}
                  items={isUsMarket ? timeZonesUS : timeZonesEU}
                  margin="dense"
                  size="small"
                  required
                  fullWidth
                  defaultValue={!isUsMarket ? TimeZone.Stockholm : null}
                >
                  {({ label }) => <Text>{label}</Text>}
                </CSelect>
              </Grid>
              {!isUsMarket && (
                <Grid item xs={12}>
                  <CSelect
                    name="country"
                    label={t("form.country")}
                    items={countriesEU}
                    margin="dense"
                    size="small"
                    required
                    fullWidth
                    defaultValue={Country.SE}
                  >
                    {({ label }) => <Text>{label}</Text>}
                  </CSelect>
                </Grid>
              )}
              {isUsMarket && (
                <Grid item xs={12}>
                  <MultiSelect
                    getOptionLabel={({ value, code }) => `${value} (${code})`}
                    getOptionValue={opt => opt.code}
                    loading={licenses.isLoading}
                    noOptionsText={licenses.error ? <ErrorBox error={licenses.error} /> : ""}
                    name="us_licenses"
                    options={licenses.data ?? []}
                    label={t`form.state_licenses`}
                    required
                    margin="dense"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <label role="button" className={styles.label}>
                  <Text small light>
                    {t("form.available_for_patients")}
                  </Text>
                  <Text small ml="auto">
                    {availableForPatients ? t("buttons.yes") : t("buttons.no")}
                  </Text>
                  <Switch color="primary" inputRef={form.register} name="available_for_patients" />
                </label>
              </Grid>
            </Grid>
            {step1Error && (
              <Text small paragraph mb={0} mt={1} style={{ color: "#f44336" }}>
                Please fill in all required fields
              </Text>
            )}
            <HFlex justifyContent="flex-end" my={3}>
              <Button variant="contained" color="primary" onClick={goNext}>
                {t("buttons.next")}
              </Button>
            </HFlex>
          </div>

          {/* Step 2: Organization & Configuration */}
          <div style={{ display: step === 2 ? undefined : "none" }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <CAutocomplete
                  name="health_care_provider_name"
                  label={t("form.health_care_provider")}
                  options={mappedHCPs}
                  getOptionLabel={(opt: { label: string }) => opt.label}
                  getOptionValue={(opt: { value: string }) => opt.value}
                  required
                  margin="dense"
                />
              </Grid>
            </Grid>
            <Text paragraph caption secondary mt={2}>
              {t("form.treatment_enrollments")}
            </Text>
            <Pane dark p={1}>
              <StyledFormGroup row>
                {treatmentEnrollments.map(joint => (
                  <FormControlLabel
                    key={joint}
                    labelPlacement="start"
                    label={<Text caption>{capitalizeFirstLetter(parseJointName(joint))}</Text>}
                    control={
                      <Switch
                        checked={!!joints[joint]}
                        onChange={() => setJoints(prev => ({ ...prev, [joint]: !prev[joint] }))}
                        color="primary"
                      />
                    }
                  />
                ))}
                <FormControlLabel
                  key="all_joints"
                  labelPlacement="start"
                  label={<Text caption>{t("therapist_profile.all_joints")}</Text>}
                  control={
                    <Switch
                      checked={treatmentEnrollments.every(j => !!joints[j])}
                      onChange={() => {
                        const newState = !treatmentEnrollments.every(j => joints[j]);
                        setJoints(Object.fromEntries(treatmentEnrollments.map(j => [j, newState])));
                      }}
                      color="primary"
                    />
                  }
                />
              </StyledFormGroup>
            </Pane>
            <HFlex justifyContent="space-between" my={3}>
              <Button onClick={() => setStep(1)}>{t("buttons.previous")}</Button>
              <ProgressButton loading={isLoading} endIcon={<Send width={20} />}>
                {t("buttons.send_invitation")}
              </ProgressButton>
            </HFlex>
          </div>
        </form>
      </FormProvider>
    </Container>
  );
};

const styles = {
  label: css`
    display: flex;
    align-items: center;
  `,
};
